import React, { useCallback, useMemo, useRef } from 'react'
import {
  Paper,
  IconButton,
  Chip,
  Box,
  Typography,
  Tooltip,
  useTheme,
  Button,
  Rating,
} from '@mui/material'
import {
  Edit,
  Delete,
  LocationOn,
  Build,
  CarRepair,
  Person,
  Phone,
  Notes,
  Save,
  DirectionsCar,
} from '@mui/icons-material'
import { OtwAgGridReact as AgGridReact } from '@/components/AgGrid/OtwAgGridReact'
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  RowClassParams,
  ValueGetterParams,
} from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { PickupLocation, RepairShopGrade, ShopType, RentalCompanyLocation, VendorLocation, TowCompanyLocation } from '@otw/models'
import { isRepairShopActive } from '@/hooks/inventory/useRepairShopLocations'
import { formatRepairShopCapacityLabel, type RepairShopTruckCounts } from '@/hooks/logistics/useLogistics'
import { formatCurrency } from '@/utils/formatCurrency'
import { MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import { useIsMobile } from 'hooks/screen/useIsMobile'

// Extend PickupLocation to include shop type for repair shops
interface RepairShopLocation extends PickupLocation {
  shop_type?: ShopType[]
  name?: string
  grade?: RepairShopGrade
  communication?: number // 1-5 star rating
  price?: number // 1-5 star rating
  speed?: number // 1-5 star rating
  labor_rate?: number // Optional labor rate
  capacity?: number // Optional capacity
  secondary_phone?: string // Optional secondary phone number
  is_active?: boolean
  inactive_at?: Date | null
  inactive_note?: string | null
}

type ManagedLocation = PickupLocation | RepairShopLocation | RentalCompanyLocation | VendorLocation | TowCompanyLocation
type ManagedLocationType = 'pickup' | 'repair_shop' | 'rental_company' | 'vendor' | 'tow_company'

const locationTypeNoun = (locationType: ManagedLocationType) => {
  switch (locationType) {
    case 'pickup':
      return 'pickup'
    case 'rental_company':
      return 'rental company'
    case 'vendor':
      return 'vendor'
    case 'tow_company':
      return 'tow company'
    default:
      return 'repair shop'
  }
}

interface LocationTableProps {
  locations: ManagedLocation[]
  locationType: ManagedLocationType
  onEdit: (location: ManagedLocation) => void
  onDelete?: (id: string) => void
  onRatingUpdate?: (locationId: string, field: 'communication' | 'price' | 'speed', value: number | null) => void
  repairShopTruckCountsById?: Record<string, RepairShopTruckCounts>
  repairShopSpendingById?: Record<string, number>
  capacityNumberFormatter?: Intl.NumberFormat
}

const LocationTable: React.FC<LocationTableProps> = ({
  locations,
  locationType,
  onEdit,
  onDelete,
  onRatingUpdate,
  repairShopTruckCountsById,
  repairShopSpendingById,
  capacityNumberFormatter,
}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const gridApiRef = useRef<GridApi | null>(null)

  const locationTitle = (location: ManagedLocation) => {
    const row = location as PickupLocation & RepairShopLocation & { business_name?: string }
    return row.company_name || row.name || row.business_name || 'Location'
  }

  const locationMeta = (location: ManagedLocation) => {
    const row = location as PickupLocation
    return [row.address, row.city, row.state].filter(Boolean).join(', ')
  }

  const getShopTypeLabel = (shopType: ShopType) => {
    switch (shopType) {
      case 'mechanic_shop':
        return 'Mechanic'
      case 'body_shop':
        return 'Body Shop'
      case 'parts':
        return 'Parts'
      case 'other':
        return 'Other'
      case 'mobile':
        return 'Mobile'
      default:
        return 'Unknown'
    }
  }

  const getShopTypeColor = (shopType: ShopType) => {
    switch (shopType) {
      case 'mechanic_shop':
        return 'primary'
      case 'body_shop':
        return 'secondary'
      case 'parts':
        return 'info'
      case 'other':
        return 'default'
      case 'mobile':
        return 'success'
      default:
        return 'default'
    }
  }

  const getShopTypeIcon = (shopType: ShopType) => {
    switch (shopType) {
      case 'mechanic_shop':
        return <Build fontSize="small" />
      case 'body_shop':
        return <CarRepair fontSize="small" />
      case 'parts':
        return <LocationOn fontSize="small" />
      case 'mobile':
        return <DirectionsCar fontSize="small" />
      default:
        return <LocationOn fontSize="small" />
    }
  }

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api
  }, [])

  // Shop Type renderer (for repair shops only)
  const ShopTypeRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    const shopTypes = location.shop_type || []
    
    if (shopTypes.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    
    const maxVisible = 1 // Show first chip
    const visible = shopTypes.slice(0, maxVisible)
    const remaining = shopTypes.slice(maxVisible)
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {visible.map((type, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
            {getShopTypeIcon(type)}
            <Chip
              label={getShopTypeLabel(type)}
              color={getShopTypeColor(type) as any}
              size="small"
              sx={{ 
                ml: 0.5,
                height: 20, 
                fontSize: '0.7rem',
                maxWidth: '100px',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
              }}
            />
          </Box>
        ))}
        {remaining.length > 0 && (
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                  All Shop Types:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {shopTypes.map((type, index) => (
                    <Typography key={index} variant="caption" sx={{ display: 'block' }}>
                      • {getShopTypeLabel(type)}
                    </Typography>
                  ))}
                </Box>
              </Box>
            }
            arrow
            placement="top"
          >
            <Chip
              label={`+${remaining.length} more`}
              size="small"
              sx={{ 
                height: 20, 
                fontSize: '0.7rem',
                cursor: 'pointer',
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.08)',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.15)' 
                    : 'rgba(0, 0, 0, 0.12)',
                }
              }}
            />
          </Tooltip>
        )}
      </Box>
    )
  }

  // Vendor / repair shop / tow company name cell
  const VendorNameRenderer = (props: ICellRendererParams) => {
    const location = props.data as VendorLocation | TowCompanyLocation
    return (
      <Typography variant="body2" fontWeight="medium">
        {location.name || '—'}
      </Typography>
    )
  }

  const StatusRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    if (!location) return null
    const inactive = !isRepairShopActive(location)
    const chip = (
      <Chip
        label={inactive ? 'Inactive' : 'Active'}
        size="small"
        color={inactive ? 'warning' : 'success'}
        variant={inactive ? 'filled' : 'outlined'}
        sx={{ height: 22, fontWeight: 700, fontSize: '0.75rem' }}
      />
    )
    if (inactive && location.inactive_note) {
      return (
        <Tooltip title={location.inactive_note} arrow>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{chip}</Box>
        </Tooltip>
      )
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{chip}</Box>
    )
  }

  // Name renderer (for repair shops only)
  const NameRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    return (
      <Typography variant="body2" fontWeight="medium">
        {location.name || '—'}
      </Typography>
    )
  }

  // Grade renderer (for repair shops only)
  const GradeRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    return (
      <Typography variant="body2" fontWeight="medium">
        {location.grade || '—'}
      </Typography>
    )
  }

  // Business name renderer (for rental companies only)
  const BusinessNameRenderer = (props: ICellRendererParams) => {
    const location = props.data as RentalCompanyLocation
    return (
      <Typography variant="body2" fontWeight="medium">
        {location.business_name || '—'}
      </Typography>
    )
  }

  // Vehicle types renderer (for rental companies only)
  const VehicleTypesRenderer = (props: ICellRendererParams) => {
    const location = props.data as RentalCompanyLocation
    const types = location.vehicle_types || []
    if (types.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    const maxVisible = 4
    const visible = types.slice(0, maxVisible)
    const remaining = types.slice(maxVisible)
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {visible.map((type, index) => (
          <Chip key={index} label={type} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
        ))}
        {remaining.length > 0 && (
          <Tooltip title={remaining.join(', ')} arrow placement="top">
            <Chip
              label={`+${remaining.length}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
              }}
            />
          </Tooltip>
        )}
      </Box>
    )
  }

  // States rent in renderer (for rental companies only)
  const StatesRentInRenderer = (props: ICellRendererParams) => {
    const location = props.data as RentalCompanyLocation
    const statesList = location.states_rent_in || []
    if (statesList.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    return (
      <Typography variant="body2">
        {statesList.join(', ')}
      </Typography>
    )
  }

  // Fleet size renderer (for rental companies only)
  const FleetSizeRenderer = (props: ICellRendererParams) => {
    const location = props.data as RentalCompanyLocation
    const fleetSize = location.fleet_size
    if (fleetSize == null) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    return <Typography variant="body2">{fleetSize}</Typography>
  }

  // Charge per week renderer (for rental companies only)
  const ChargePerWeekRenderer = (props: ICellRendererParams) => {
    const location = props.data as RentalCompanyLocation
    const min = location.charge_per_week_min
    const max = location.charge_per_week_max
    if (min == null && max == null) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    if (min != null && max != null) {
      return (
        <Typography variant="body2">
          ${min.toLocaleString()} – ${max.toLocaleString()}/wk
        </Typography>
      )
    }
    if (min != null) {
      return (
        <Typography variant="body2">
          ${min.toLocaleString()}+/wk
        </Typography>
      )
    }
    return (
      <Typography variant="body2">
        Up to ${max!.toLocaleString()}/wk
      </Typography>
    )
  }

  // Street renderer
  const CompanyRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation
    return (
      <Typography variant="body2" fontWeight="medium">
        {location.company_name || '—'}
      </Typography>
    )
  }

  // Street renderer
  const StreetRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation | RepairShopLocation | RentalCompanyLocation
    return (
      <Typography variant="body2" fontWeight="medium">
        {location.address || '—'}
                    </Typography>
    )
  }

  // City renderer
  const CityRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation | RepairShopLocation | RentalCompanyLocation
    return (
      <Typography variant="body2">
        {location.city || '—'}
                  </Typography>
    )
  }

  // State renderer
  const StateRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation | RepairShopLocation | RentalCompanyLocation
    return (
      <Typography variant="body2">
        {location.state || '—'}
                </Typography>
    )
  }

  // Zip renderer
  const ZipRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation | RepairShopLocation | RentalCompanyLocation
    return (
      <Typography variant="body2">
        {location.zip || '—'}
                    </Typography>
    )
  }

  // Contact renderer
  const ContactRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation | RepairShopLocation | RentalCompanyLocation
    if (!location.contact_name) {
  return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
            return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Person sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                      <Typography variant="body2">{location.contact_name}</Typography>
                    </Box>
    )
  }

  // Phone renderer
  const PhoneRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation | RepairShopLocation | RentalCompanyLocation
    if (!location.contact_phone) {
      return (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
      )
    }
    return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Phone sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                      <Typography variant="body2">{location.contact_phone}</Typography>
                    </Box>
    )
  }

  const SecondaryContactRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation
    if (!location.secondary_contact_name) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Person sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
        <Typography variant="body2">{location.secondary_contact_name}</Typography>
      </Box>
    )
  }

  const SecondaryContactPhoneRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation
    if (!location.secondary_contact_phone) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Phone sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
        <Typography variant="body2">{location.secondary_contact_phone}</Typography>
      </Box>
    )
  }

  // Secondary phone renderer (for repair shops only)
  const SecondaryPhoneRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    if (!location.secondary_phone) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Phone sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
        <Typography variant="body2">{location.secondary_phone}</Typography>
      </Box>
    )
  }

  // Notes renderer
  const NotesRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation | RepairShopLocation | RentalCompanyLocation
    if (!location.notes) {
      return (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
      )
    }
    return (
                    <Tooltip title={location.notes} placement="top">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Notes sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 150,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {location.notes}
                        </Typography>
                      </Box>
                    </Tooltip>
    )
  }

  // Communication rating renderer (for repair shops only)
  const CommunicationRatingRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    const handleChange = (_event: React.SyntheticEvent, newValue: number | null) => {
      if (location.id && onRatingUpdate) {
        onRatingUpdate(location.id, 'communication', newValue)
      }
    }
    return (
      <Rating
        value={location.communication || 0}
        onChange={handleChange}
        max={5}
        size="small"
        sx={{ '& .MuiRating-iconFilled': { color: 'primary.main' } }}
      />
    )
  }

  // Price rating renderer (for repair shops only)
  const PriceRatingRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    const handleChange = (_event: React.SyntheticEvent, newValue: number | null) => {
      if (location.id && onRatingUpdate) {
        onRatingUpdate(location.id, 'price', newValue)
      }
    }
    return (
      <Rating
        value={location.price || 0}
        onChange={handleChange}
        max={5}
        size="small"
        sx={{ '& .MuiRating-iconFilled': { color: 'primary.main' } }}
      />
    )
  }

  // Speed rating renderer (for repair shops only)
  const SpeedRatingRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    const handleChange = (_event: React.SyntheticEvent, newValue: number | null) => {
      if (location.id && onRatingUpdate) {
        onRatingUpdate(location.id, 'speed', newValue)
      }
    }
    return (
      <Rating
        value={location.speed || 0}
        onChange={handleChange}
        max={5}
        size="small"
        sx={{ '& .MuiRating-iconFilled': { color: 'primary.main' } }}
      />
    )
  }

  // Labor rate renderer (for repair shops only)
  const LaborRateRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    if (location.labor_rate === undefined || location.labor_rate === null) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    return (
      <Typography variant="body2" fontWeight="medium">
        ${location.labor_rate.toFixed(2)}/hr
      </Typography>
    )
  }

  // Total spent renderer (for repair shops only)
  const TotalSpentRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    const total = location.id && repairShopSpendingById
      ? repairShopSpendingById[location.id] ?? 0
      : 0
    return (
      <Typography variant="body2" fontWeight="medium">
        {formatCurrency(total) || '$0.00'}
      </Typography>
    )
  }

  // Capacity renderer (for repair shops only)
  const CapacityRenderer = (props: ICellRendererParams) => {
    const location = props.data as RepairShopLocation
    if (location.capacity === undefined || location.capacity === null) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    if (!repairShopTruckCountsById || !capacityNumberFormatter) {
      return <Typography variant="body2">{location.capacity}</Typography>
    }
    const counts = location.id ? repairShopTruckCountsById[location.id] : undefined
    return (
      <Typography variant="body2">
        {formatRepairShopCapacityLabel(location, counts, capacityNumberFormatter)}
      </Typography>
    )
  }

  // Actions renderer
  const ActionsRenderer = (props: ICellRendererParams) => {
    const location = props.data as PickupLocation | RepairShopLocation | RentalCompanyLocation
    // Check if this is a previous address (not editable, only saveable)
    const isPreviousAddress = location.id?.startsWith('previous-')
    
    return (
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
        {!isPreviousAddress && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
              onClick={() => onEdit(location)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
        )}
                    {onDelete ? (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
              onClick={() => location.id && onDelete(location.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Save to Locations">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Save />}
              onClick={() => onEdit(location)}
                        >
                          Save
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
    )
  }

  const TowingRadiusRenderer = (props: ICellRendererParams) => {
    const location = props.data as TowCompanyLocation
    const miles = location.towing_radius_miles
    if (miles == null) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    return (
      <Typography variant="body2">
        {miles.toLocaleString('en-US')} mi
      </Typography>
    )
  }

  const columns: ColDef[] = useMemo(() => {
    const baseColumns: ColDef[] = [
      {
        field: 'actions',
        headerName: 'Actions',
        width: 150,
        cellRenderer: ActionsRenderer,
        sortable: false,
        filter: false,
        pinned: 'right',
      },
    ]

    // Vendor: name column (no company / secondary contact)
    if (locationType === 'vendor') {
      baseColumns.unshift({
        field: 'name',
        headerName: 'Vendor name',
        width: 220,
        cellRenderer: VendorNameRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        valueGetter: (params: ValueGetterParams) => {
          const location = params.data as VendorLocation
          return location.name || ''
        },
      })
    }

    if (locationType === 'tow_company') {
      baseColumns.unshift(
        {
          field: 'name',
          headerName: 'Company name',
          width: 220,
          cellRenderer: VendorNameRenderer,
          sortable: true,
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as TowCompanyLocation
            return location.name || ''
          },
        },
        {
          field: 'grade',
          headerName: 'Grade',
          width: 120,
          cellRenderer: GradeRenderer,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          filterParams: {
            values: ['A', 'B', 'C'],
          },
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as TowCompanyLocation
            return location.grade || null
          },
        },
        {
          field: 'communication',
          headerName: 'Communication',
          width: 160,
          cellRenderer: CommunicationRatingRenderer,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          filterParams: {
            values: [0, 1, 2, 3, 4, 5],
            valueFormatter: (params: { value: number }) => {
              if (params.value === 0) return 'No rating'
              return `${params.value} ${params.value === 1 ? 'star' : 'stars'}`
            },
          },
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as TowCompanyLocation
            return location.communication || 0
          },
        },
        {
          field: 'price',
          headerName: 'Price',
          width: 140,
          cellRenderer: PriceRatingRenderer,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          filterParams: {
            values: [0, 1, 2, 3, 4, 5],
            valueFormatter: (params: { value: number }) => {
              if (params.value === 0) return 'No rating'
              return `${params.value} ${params.value === 1 ? 'star' : 'stars'}`
            },
          },
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as TowCompanyLocation
            return location.price || 0
          },
        },
        {
          field: 'speed',
          headerName: 'Speed',
          width: 140,
          cellRenderer: SpeedRatingRenderer,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          filterParams: {
            values: [0, 1, 2, 3, 4, 5],
            valueFormatter: (params: { value: number }) => {
              if (params.value === 0) return 'No rating'
              return `${params.value} ${params.value === 1 ? 'star' : 'stars'}`
            },
          },
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as TowCompanyLocation
            return location.speed || 0
          },
        },
        {
          field: 'towing_radius_miles',
          headerName: 'Towing radius',
          width: 150,
          cellRenderer: TowingRadiusRenderer,
          sortable: true,
          filter: 'agNumberColumnFilter',
          floatingFilter: true,
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as TowCompanyLocation
            return location.towing_radius_miles ?? null
          },
        },
      )
    }

    // Add rental company specific columns
    if (locationType === 'rental_company') {
      baseColumns.unshift(
        {
          field: 'business_name',
          headerName: 'Business Name',
          width: 220,
          cellRenderer: BusinessNameRenderer,
          sortable: true,
          filter: 'agTextColumnFilter',
          floatingFilter: true,
        },
        {
          field: 'vehicle_types',
          headerName: 'Vehicle Types',
          width: 340,
          cellRenderer: VehicleTypesRenderer,
          sortable: true,
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RentalCompanyLocation
            return (location.vehicle_types || []).join(', ') || '—'
          },
        },
        {
          field: 'states_rent_in',
          headerName: 'States They Rent In',
          width: 200,
          cellRenderer: StatesRentInRenderer,
          sortable: true,
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RentalCompanyLocation
            return (location.states_rent_in || []).join(', ') || '—'
          },
        },
        {
          field: 'fleet_size',
          headerName: 'Fleet Size',
          width: 150,
          cellRenderer: FleetSizeRenderer,
          sortable: true,
          filter: 'agNumberColumnFilter',
          floatingFilter: true,
        },
        {
          field: 'charge_per_week',
          headerName: 'Charge/Week',
          width: 160,
          cellRenderer: ChargePerWeekRenderer,
          sortable: true,
          filter: false,
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RentalCompanyLocation
            const min = location.charge_per_week_min
            const max = location.charge_per_week_max
            if (min != null && max != null) return min
            if (min != null) return min
            if (max != null) return max
            return null
          },
        }
      )
    }

    // Add repair shop specific columns
    if (locationType === 'repair_shop') {
      baseColumns.unshift(
        {
          field: 'is_active',
          headerName: 'Status',
          width: 120,
          pinned: 'left',
          cellRenderer: StatusRenderer,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          filterParams: {
            values: ['Active', 'Inactive'],
          },
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RepairShopLocation
            return location && !isRepairShopActive(location) ? 'Inactive' : 'Active'
          },
        },
        {
          field: 'shop_type',
          headerName: 'Shop Type',
          width: 220,
          cellRenderer: ShopTypeRenderer,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RepairShopLocation
            const shopTypes = location.shop_type || []
            return shopTypes.map(type => getShopTypeLabel(type)).join(', ') || '—'
          },
        },
        {
          field: 'name',
          headerName: 'Name',
          width: 200,
          cellRenderer: NameRenderer,
          sortable: true,
          filter: 'agTextColumnFilter',
          floatingFilter: true,
        },
        {
          field: 'grade',
          headerName: 'Grade',
          width: 120,
          cellRenderer: GradeRenderer,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          filterParams: {
            values: ['A', 'B', 'C'],
          },
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RepairShopLocation
            return location.grade || null
          },
        },
        {
          field: 'communication',
          headerName: 'Communication',
          width: 160,
          cellRenderer: CommunicationRatingRenderer,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          filterParams: {
            values: [0, 1, 2, 3, 4, 5],
            valueFormatter: (params: any) => {
              if (params.value === 0) return 'No rating'
              return `${params.value} ${params.value === 1 ? 'star' : 'stars'}`
            },
          },
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RepairShopLocation
            return location.communication || 0
          },
        },
        {
          field: 'price',
          headerName: 'Price',
          width: 140,
          cellRenderer: PriceRatingRenderer,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          filterParams: {
            values: [0, 1, 2, 3, 4, 5],
            valueFormatter: (params: any) => {
              if (params.value === 0) return 'No rating'
              return `${params.value} ${params.value === 1 ? 'star' : 'stars'}`
            },
          },
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RepairShopLocation
            return location.price || 0
          },
        },
        {
          field: 'speed',
          headerName: 'Speed',
          width: 140,
          cellRenderer: SpeedRatingRenderer,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          filterParams: {
            values: [0, 1, 2, 3, 4, 5],
            valueFormatter: (params: any) => {
              if (params.value === 0) return 'No rating'
              return `${params.value} ${params.value === 1 ? 'star' : 'stars'}`
            },
          },
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RepairShopLocation
            return location.speed || 0
          },
        },
        {
          field: 'labor_rate',
          headerName: 'Labor Rate',
          width: 140,
          cellRenderer: LaborRateRenderer,
          sortable: true,
          filter: 'agNumberColumnFilter',
          floatingFilter: true,
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RepairShopLocation
            return location.labor_rate ?? null
          },
        },
        {
          field: 'capacity',
          headerName: 'Capacity',
          width: 180,
          cellRenderer: CapacityRenderer,
          sortable: true,
          filter: 'agNumberColumnFilter',
          floatingFilter: true,
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RepairShopLocation
            return location.capacity ?? null
          },
        },
        {
          field: 'total_spent',
          headerName: 'Total Spent',
          width: 140,
          cellRenderer: TotalSpentRenderer,
          sortable: true,
          filter: 'agNumberColumnFilter',
          floatingFilter: true,
          valueGetter: (params: ValueGetterParams) => {
            const location = params.data as RepairShopLocation
            if (!location.id || !repairShopSpendingById) return 0
            return repairShopSpendingById[location.id] ?? 0
          },
        }
      )
    }

    // Add common columns
    baseColumns.splice(-1, 0,
      ...(locationType === 'pickup' ? [{
        field: 'company_name',
        headerName: 'Company',
        width: 220,
        cellRenderer: CompanyRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        valueGetter: (params: ValueGetterParams) => {
          const location = params.data as PickupLocation
          return location.company_name || ''
        },
      }] : []),
      {
        field: 'address',
        headerName: 'Street',
        width: 300,
        cellRenderer: StreetRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        valueGetter: (params: ValueGetterParams) => {
          const location = params.data as ManagedLocation
          return location.address || ''
        },
      },
      {
        field: 'city',
        headerName: 'City',
        width: 200,
        cellRenderer: CityRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        valueGetter: (params: ValueGetterParams) => {
          const location = params.data as ManagedLocation
          return location.city || ''
        },
      },
      {
        field: 'state',
        headerName: 'State',
        width: 120,
        cellRenderer: StateRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        valueGetter: (params: ValueGetterParams) => {
          const location = params.data as ManagedLocation
          return location.state || ''
        },
      },
      {
        field: 'zip',
        headerName: 'Zip',
        width: 120,
        cellRenderer: ZipRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        valueGetter: (params: ValueGetterParams) => {
          const location = params.data as ManagedLocation
          return location.zip || ''
        },
      },
      {
        field: 'contact_name',
        headerName: 'Contact',
        width: 300,
        cellRenderer: ContactRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
      },
      {
        field: 'contact_phone',
        headerName: 'Phone',
        width: 180,
        cellRenderer: PhoneRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
      },
      ...(locationType === 'pickup' ? [
      {
        field: 'secondary_contact_name',
        headerName: 'Secondary Contact',
        width: 220,
        cellRenderer: SecondaryContactRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
      },
      {
        field: 'secondary_contact_phone',
        headerName: 'Secondary Phone',
        width: 180,
        cellRenderer: SecondaryContactPhoneRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
      }] : []),
      ...(locationType === 'repair_shop' ? [{
        field: 'secondary_phone',
        headerName: 'Secondary Phone',
        width: 180,
        cellRenderer: SecondaryPhoneRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
      }] : []),
      {
        field: 'notes',
        headerName: 'Notes',
        width: 250,
        cellRenderer: NotesRenderer,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
      }
    )

    return baseColumns
  }, [locationType, onEdit, onDelete, onRatingUpdate, repairShopSpendingById])

  const defaultColDef = {
    sortable: true,
    resizable: true,
  }

  const memoizedRowData = useMemo(() => {
    return locations.filter(location => location && location.id)
  }, [locations])

  const getRowStyle = useCallback(
    (params: RowClassParams) => {
      if (locationType !== 'repair_shop') return undefined
      const location = params.data as RepairShopLocation | undefined
      if (!location || isRepairShopActive(location)) return undefined
      return {
        backgroundColor:
          theme.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.16)' : 'rgba(237, 108, 2, 0.12)',
      }
    },
    [locationType, theme.palette.mode],
  )

  if (!locations || locations.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <LocationOn sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No {locationTypeNoun(locationType)} locations found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add your first {locationTypeNoun(locationType)} location to get started
        </Typography>
      </Paper>
    )
  }

  if (isMobile) {
    return (
      <MobileRecordList>
        {memoizedRowData.map(location => (
          <MobileRecordCard
            key={location.id}
            title={locationTitle(location)}
            meta={locationMeta(location)}
            detail={locationTypeNoun(locationType)}
            onClick={() => onEdit(location)}
          />
        ))}
      </MobileRecordList>
    )
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 300px)',
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        className={theme.palette.mode === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
        style={{ height: '100%', width: '100%', flex: 1 }}
      >
        <AgGridReact
          rowData={memoizedRowData}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          pagination={true}
          paginationPageSize={20}
          rowSelection='multiple'
          enableCellTextSelection={true}
          suppressRowClickSelection={true}
          animateRows={false}
          suppressScrollOnNewData={true}
          rowBuffer={50}
          getRowId={params => params.data.id}
          getRowStyle={getRowStyle}
          overlayNoRowsTemplate={`No ${locationTypeNoun(locationType)} locations found`}
          suppressRowTransform={true}
          suppressColumnVirtualisation={false}
          suppressRowVirtualisation={false}
        />
      </div>
    </Box>
  )
}

export default LocationTable 